// Re-apply the user's unsaved grid edits onto freshly loaded rows.
//
// After a 409 the grid reloads so the user sees what the other person saved,
// but their own changed cells must survive the reload: each changed field of
// an edited row is copied over the fresh row with the same key, and an edited
// row whose key the server does not have yet (an uploaded new lot) is kept as
// is. Tokens are NOT carried here: the next save takes MODIFIED_DATE from the
// fresh load, so re-saving is a deliberate overwrite after the user has seen
// the latest data.
//
// `derive(row, changedFields)` (optional) recomputes fields that depend on
// others (OTTI's SPRD_FLG / OTTI_FLG): a merged row mixes the user's inputs
// with the other person's, so derived values from either side can be stale.
// It returns the derived fields to set, or null. A derived field whose value
// differs from the fresh row becomes a changed cell too, so it is saved.
//
// Returns { rows, changedCells } without mutating the inputs.
export const reapplyEdits = (freshRows, editedRows, changedCells, keyOf, derive = null) => {
  const cells = new Map([...changedCells].map(([key, fields]) => [key, new Set(fields)]));
  const editedByKey = new Map();
  (editedRows || []).forEach((row) => {
    const key = keyOf(row);
    if (cells.has(key)) editedByKey.set(key, row);
  });

  const seen = new Set();
  const rows = (freshRows || []).map((row) => {
    const key = keyOf(row);
    const edited = editedByKey.get(key);
    if (!edited) return row;
    seen.add(key);
    const fields = cells.get(key);
    const next = { ...row };
    fields.forEach((field) => {
      next[field] = edited[field];
    });
    const derived = derive ? derive(next, fields) : null;
    Object.entries(derived || {}).forEach(([field, value]) => {
      next[field] = value;
      if (value !== row[field]) fields.add(field);
    });
    return next;
  });

  editedByKey.forEach((row, key) => {
    if (!seen.has(key)) rows.push(row);
  });
  return { rows, changedCells: cells };
};
